import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

import {
  CheckInDisplayEvent,
  CheckInDisplayItem,
  CheckInEvent,
} from './check-ins.model';
import {
  AnimalEventStatus,
  resolveAnimalEventStatus,
} from '../../utils/animal-event-status.util';
import {
  formatUtcDate,
  getDateTimestamp,
} from '../../utils/date-format-utc.util';
import { IconComponent } from "../../design-system/components/icon/icon.component";

@Component({
  selector: 'lib-check-ins',
  standalone: true,
  imports: [
    IconComponent
  ],
  templateUrl: './check-ins.component.html',
  styleUrl: './check-ins.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInsComponent {
  readonly events = input<readonly CheckInEvent[]>([]);

  readonly isLoading = input(false);

  readonly errorMessage = input<string | null>(null);

  readonly allowOwnerNavigation = input(false);

  readonly viewOwner = output<string>();

  readonly checkIns = computed<CheckInDisplayItem[]>(() => {
    const ownerGroups = this.groupEventsByOwner(this.events());

    return Array.from(ownerGroups.entries())
      .sort(([, leftEvents], [, rightEvents]) => {
        const leftLatestEvent = leftEvents[leftEvents.length - 1];

        const rightLatestEvent = rightEvents[rightEvents.length - 1];

        return (
          getDateTimestamp(rightLatestEvent?.eventDate) -
          getDateTimestamp(leftLatestEvent?.eventDate)
        );
      })
      .map(([ownerKey, ownerEvents]) =>
        this.createCheckIn(ownerKey, ownerEvents),
      );
  });

  isAdopterUnknown(e: CheckInDisplayItem): boolean {
    return e.ownerName.toLowerCase().includes('unknown');
  }

  onViewOwner(petszelOwnerId: string): void {
    const normalizedId = petszelOwnerId.trim();

    if (!this.allowOwnerNavigation() || !normalizedId) {
      return;
    }

    this.viewOwner.emit(normalizedId);
  }

  private groupEventsByOwner(
    events: readonly CheckInEvent[],
  ): Map<string, CheckInEvent[]> {
    const groups = new Map<string, CheckInEvent[]>();

    events.forEach((event, index) => {
      const ownerId = event.petszelOwnerId?.trim();

      /*
       * Events without an owner ID remain separate
       * instead of accidentally being grouped.
       */
      const ownerKey = ownerId || event.id?.trim() || `unknown-owner-${index}`;

      const existingEvents = groups.get(ownerKey) ?? [];

      existingEvents.push(event);

      groups.set(ownerKey, existingEvents);
    });

    groups.forEach((ownerEvents) => {
      ownerEvents.sort(
        (left, right) =>
          getDateTimestamp(left.eventDate) - getDateTimestamp(right.eventDate),
      );
    });

    return groups;
  }

  private createCheckIn(
    ownerKey: string,
    events: CheckInEvent[],
  ): CheckInDisplayItem {
    const firstEvent = events[0];

    const lastEvent = events[events.length - 1];

    const hasDistinctEnd = events.length > 1 && lastEvent.id !== firstEvent.id;

    const rawStatus =
      lastEvent.petszelStatus?.trim() ||
      lastEvent.subEvent?.trim() ||
      lastEvent.event?.trim() ||
      'Current';

    const status = resolveAnimalEventStatus(rawStatus);

    return {
      id: `${ownerKey}-` + `${firstEvent.id}`,

      petszelOwnerId: firstEvent.petszelOwnerId?.trim() || '',

      ownerName: this.formatOwnerName(firstEvent),

      status: status.label,

      statusClass: this.getStatusClass(status.status),

      start: this.createDisplayEvent(firstEvent),

      end: hasDistinctEnd ? this.createDisplayEvent(lastEvent) : null,
    };
  }

  private createDisplayEvent(event: CheckInEvent): CheckInDisplayEvent {
    return {
      date: formatUtcDate(event.eventDate),

      description: this.formatDescription(event),
    };
  }

  private formatOwnerName(event: CheckInEvent): string {
    const name = [event.firstname?.trim(), event.lastname?.trim()]
      .filter(Boolean)
      .join(' ');

    return name || 'Unknown pet parent';
  }

  private formatDescription(event: CheckInEvent): string {
    const metadata = event.metaData ?? {};

    const location =
      metadata['location']?.trim() ||
      metadata['shelterLocation']?.trim() ||
      metadata['Location']?.trim() ||
      '';

    return this.uniqueValues([
      this.formatEventName(event.event),
      event.subEvent?.trim(),
      location,
    ]).join(', ');
  }

  private uniqueValues(values: Array<string | null | undefined>): string[] {
    const seen = new Set<string>();

    return values.filter((value): value is string => {
      const normalizedValue = value?.trim();

      if (!normalizedValue) {
        return false;
      }

      const comparisonValue = normalizedValue.toLowerCase();

      if (seen.has(comparisonValue)) {
        return false;
      }

      seen.add(comparisonValue);

      return true;
    });
  }

  private getStatusClass(status: AnimalEventStatus): string {
    return `check-ins__status--${status}`;
  }

  private formatEventName(value: string): string {
    const normalizedValue = value?.trim();

    if (!normalizedValue) {
      return '';
    }

    const separatorIndex = normalizedValue.lastIndexOf('.');

    return separatorIndex >= 0
      ? normalizedValue.slice(separatorIndex + 1).trim()
      : normalizedValue;
  }
}
